import { create } from 'zustand';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';
import { IKpiTemplate } from '@/types/kpi-template.types';
import {
  getKpiTemplates,
  getKpiTemplateDetail,
  createKpiTemplate,
  updateKpiTemplate,
  deleteKpiTemplate,
} from '@/api/kpiTemplate';

interface PaginationParams {
  page?: number;
  page_size?: number;
  limit?: number;
  order?: 'asc' | 'desc';
  order_by?: string;
  search?: string;
  org_chart_node_id?: number;
  period?: string;
  status?: string;
}

interface PaginatedData {
  content: IKpiTemplate[];
  totalElements: number;
  totalPages: number;
}

interface KpiTemplateState {
  templates: IKpiTemplate[];
  paginatedData: PaginatedData | null;
  selectedTemplate: IKpiTemplate | null;
  isLoading: boolean;
  error: unknown;

  getTemplates: (tenantId: string | number, params?: PaginationParams) => Promise<void>;
  getTemplateDetail: (tenantId: string | number, id: string | number) => Promise<IKpiTemplate | void>;
  createTemplate: (tenantId: string | number, payload: Partial<IKpiTemplate>) => Promise<void>;
  updateTemplate: (tenantId: string | number, id: string | number, payload: Partial<IKpiTemplate>) => Promise<void>;
  deleteTemplate: (tenantId: string | number, id: string | number) => Promise<void>;
  clearTemplates: () => void;
}

const useKpiTemplateState = create<KpiTemplateState>(set => ({
  templates: [],
  paginatedData: null,
  selectedTemplate: null,
  isLoading: false,
  error: null,

  clearTemplates: () => set({ templates: [], paginatedData: null, selectedTemplate: null }),

  getTemplates: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const data = await getKpiTemplates(tenantId, params as Record<string, unknown>);
      const raw = data; // getKpiTemplates directly returns the response body
      const content: IKpiTemplate[] = raw?.data?.content ?? raw?.data ?? raw?.content ?? [];
      const total: number = raw?.data?.totalElements ?? raw?.totalElements ?? raw?.total ?? content.length;
      const limit = raw?.data?.size ?? raw?.limit ?? params?.page_size ?? params?.limit ?? 10;
      const totalPages: number = raw?.data?.totalPages ?? raw?.totalPages ?? (Math.ceil(total / limit) || 1);

      set({
        templates: content,
        paginatedData: {
          content,
          totalElements: total,
          totalPages,
        },
      });
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getTemplateDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const data = await getKpiTemplateDetail(tenantId, id);
      const detail = data.data ?? data;
      set({ selectedTemplate: detail });
      return detail;
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  createTemplate: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      await createKpiTemplate(tenantId, payload);
      toast.success('Template KPI berhasil dibuat');
    } catch (error) {
      set({ error });
      errorHandler(error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateTemplate: async (tenantId, id, payload) => {
    set({ isLoading: true, error: null });
    try {
      await updateKpiTemplate(tenantId, id, payload);
      toast.success('Template KPI berhasil diperbarui');
    } catch (error) {
      set({ error });
      errorHandler(error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteTemplate: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      await deleteKpiTemplate(tenantId, id);
      set(state => ({
        templates: state.templates.filter(t => t.id !== id),
        paginatedData: state.paginatedData
          ? {
              ...state.paginatedData,
              content: state.paginatedData.content.filter(t => t.id !== id),
              totalElements: state.paginatedData.totalElements - 1,
            }
          : null,
      }));
      toast.success('Template KPI berhasil dihapus');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },
}));

export default useKpiTemplateState;
